"use client";

import { useEffect, useState } from "react";
import { getPaymentPlanConfigs } from "@/helpers/server-actions/payment-plan-collection";
import {
  getAvailablePaymentPlans,
  calculateInstallments,
  formatInstallmentSummary,
  isPaymentPlanEligible,
} from "@/lib/payment-plans";
import { getTicketTypes } from "@/helpers/server-actions/passes-collection";
import { calculateDiscountedPrice } from "@/lib/pricing";
import { getCurrentPricingTier, getTierConfig } from "@/lib/pricing";
import type { PaymentPlanConfig, InstallmentBreakdown } from "@/models/paymentPlan";

interface CartItem {
  slug: string;
  quantity: number;
}

interface PaymentPlanSelectorProps {
  cartItems: CartItem[];
  onSelectPlan: (planConfigId: string) => void;
  onPayInFull: () => void;
}

export default function PaymentPlanSelector({
  cartItems,
  onSelectPlan,
  onPayInFull,
}: PaymentPlanSelectorProps) {
  const [plans, setPlans] = useState<PaymentPlanConfig[]>([]);
  const [breakdowns, setBreakdowns] = useState<InstallmentBreakdown[]>([]);
  const [cartTotalCents, setCartTotalCents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  useEffect(() => {
    loadPlansAndPricing();
  }, []);

  async function loadPlansAndPricing() {
    try {
      // Fetch plan configs and ticket types in parallel
      const [configs, ticketTypes] = await Promise.all([
        getPaymentPlanConfigs(2027),
        getTicketTypes(),
      ]);

      // Calculate cart total from ticket types and current pricing tier
      const tier = getCurrentPricingTier();
      let totalCents = 0;

      for (const item of cartItems) {
        const slug = item.slug.startsWith("vertical-")
          ? item.slug.replace("vertical-", "")
          : item.slug;
        const tt = ticketTypes.find((t) => t.id === slug || t.slug === slug);
        if (tt) {
          const itemPrice = calculateDiscountedPrice(tt.basePrice, tier);
          totalCents += itemPrice * item.quantity;
        }
      }

      setCartTotalCents(totalCents);

      // Filter to available plans
      const available = getAvailablePaymentPlans(configs, totalCents);
      setPlans(available);

      // Calculate breakdowns for each plan
      const bds = available.map((config) =>
        calculateInstallments(totalCents, config),
      );
      setBreakdowns(bds);

      // Pre-select the first plan
      if (available.length > 0) {
        setSelectedPlanId(available[0].id ?? null);
      }
    } catch (err) {
      console.error("Failed to load payment plans:", err);
    } finally {
      setLoading(false);
    }
  }

  const formatCad = (cents: number) =>
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);

  const tier = getCurrentPricingTier();
  const tierConfig = getTierConfig(tier);
  const eligible = isPaymentPlanEligible(cartTotalCents);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-white text-lg">Loading payment options...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white px-4 py-12">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Choose How to Pay</h1>
          <p className="text-gray-400">
            C-Tribe Festival 2027 &bull; {tierConfig.label}
          </p>
        </div>

        {/* Cart summary */}
        <div className="bg-[#1a1a1a] rounded-xl p-6 mb-6 border border-[#2a2a2a]">
          <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
            Your Cart
          </h2>
          {cartItems.map((item) => (
            <div
              key={item.slug}
              className="flex justify-between items-center py-2"
            >
              <span className="text-gray-200">
                {item.slug
                  .replace("vertical-", "")
                  .replace(/(^\w)/, (c) => c.toUpperCase())}{" "}
                Pass
                {item.quantity > 1 ? ` x${item.quantity}` : ""}
              </span>
            </div>
          ))}
          <div className="border-t border-[#333] mt-3 pt-3 flex justify-between">
            <span className="font-semibold">Total</span>
            <span className="font-semibold text-lg">
              {formatCad(cartTotalCents)}
            </span>
          </div>
        </div>

        {/* Pay in full option */}
        <button
          onClick={onPayInFull}
          className="w-full bg-[#6366f1] hover:bg-[#5558e6] text-white font-semibold py-4 px-6 rounded-xl mb-4 transition-colors text-lg"
        >
          Pay in Full — {formatCad(cartTotalCents)}
        </button>

        {/* Payment plans */}
        {eligible && plans.length > 0 ? (
          <>
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[#333]" />
              <span className="text-sm text-gray-400">
                or split into monthly payments
              </span>
              <div className="flex-1 h-px bg-[#333]" />
            </div>

            <div className="space-y-3">
              {plans.map((plan, i) => {
                const bd = breakdowns[i];
                const isSelected = selectedPlanId === plan.id;
                const cutoffDate = new Date(plan.availableUntil);
                const cutoffLabel = cutoffDate.toLocaleDateString("en-CA", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                });

                return (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id ?? null)}
                    className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
                      isSelected
                        ? "border-[#6366f1] bg-[#1a1a2e]"
                        : "border-[#2a2a2a] bg-[#1a1a1a] hover:border-[#444]"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">
                          {plan.planName}
                        </h3>
                        <p className="text-sm text-gray-400">
                          {formatInstallmentSummary(bd)}
                        </p>
                      </div>
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected
                            ? "border-[#6366f1] bg-[#6366f1]"
                            : "border-[#555]"
                        }`}
                      >
                        {isSelected && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Available until {cutoffLabel} &bull; No interest, no fees
                    </p>
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => {
                if (selectedPlanId) onSelectPlan(selectedPlanId);
              }}
              disabled={!selectedPlanId}
              className="w-full mt-4 bg-white text-black font-semibold py-4 px-6 rounded-xl transition-colors text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              Continue with Payment Plan
            </button>

            <p className="text-center text-xs text-gray-500 mt-4">
              Your pass is active immediately. No interest or fees.
              <br />
              Cancelled plans receive credit for future C-Tribe events.
            </p>
          </>
        ) : (
          !eligible && (
            <p className="text-center text-sm text-gray-500 mt-4">
              Payment plans are available for orders over $50.
            </p>
          )
        )}
      </div>
    </div>
  );
}
